import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './Card.module.scss';
import { Card /*, EmptyCard */ } from './Card';
import { CardStack } from './CardStack';
import { getSharedNumber } from '../utilities/getSharedNumber';
import { cards7 } from '../utilities/getCards';
import { randomizeIconsOnCards, randomizeIcons, IconType } from '../utilities/getIcons';

import clickAudio from '../assets/sounds/click.wav';
import { IMultiplayerData } from '../modal/Modal';

export type CardType = number[];

export interface ICardContainerProps extends React.PropsWithChildren {
    onAllDone: (winner?: IMultiplayerData) => void;
    onClickWrongIcon?: () => void;
    // Clicking disabled as a penalty for selecting the wrong buddy
    isDisabled: boolean;
    onClickSharedNumber?: (sharedNumber: number) => void;
    icons: IconType[];
    gameData: any;
    isMultiplayer?: boolean;
    isMuted?: boolean;
}

export interface ICardState {
    deckCard: number[];
    playerCard: number[];
    nextPlayerCard: number[];
    deckCardIndex: number;
    playerCardIndex: number;
    nextCardIndex: number;
}

/**
 * Holds all the cards in the game
 * @param props 
 * @returns 
 */
export function CardContainer(props: ICardContainerProps) {
    const { 
        onAllDone, 
        onClickWrongIcon, 
        isDisabled, 
        onClickSharedNumber: onClickSharedNumberProp,
        icons,
        gameData,
        isMultiplayer,
        isMuted
    } = props;

    console.log("gameData in CardContainer: ", gameData);

    // The number of icons on each card
    // const numOfIcons = 8;
    const totalCards = 20;
    const [randomizedCards, setRandomizedCards] = useState<number[][]>([]);

    const clickSound = useMemo(() => new Audio(clickAudio), []);
    
    const images = useMemo(randomizeIcons, []);

    const [cardState, setCardState] = useState<ICardState>({
        deckCard: [],
        playerCard: [],
        nextPlayerCard: [],
        deckCardIndex: 0,
        playerCardIndex: 1,
        nextCardIndex: 2
    });

    useEffect(() => {
        const deckCards = isMultiplayer ? gameData.sharedDeck : cards7;
        const playerCards = gameData?.player?.deck;
        // Only use 20 cards for single player
        // Use all cards for multiplayer
        const randomizedCardsAndIcons = isMultiplayer ? deckCards : randomizeCardsAndIcons(deckCards).slice(0,20);
        const playerCard = isMultiplayer && playerCards && playerCards.length > 0 ? 
            playerCards[playerCards.length - 1] : 
            randomizedCardsAndIcons[1];
        const nextPlayerCard = isMultiplayer && playerCards && playerCards.length > 1 ?
            playerCards[playerCards.length - 2] :
            randomizedCardsAndIcons[2];
        setRandomizedCards(randomizedCardsAndIcons);
        setCardState({
            deckCard: gameData && gameData.sharedDeck && 
                gameData.sharedDeck.length > 0 ?
                gameData.sharedDeck[gameData.sharedDeck.length - 1] :
                randomizedCardsAndIcons[0],
            playerCard: gameData && gameData.player && gameData.player.deck && 
                gameData.player.deck.length > 0 ?
                gameData.player.deck[gameData.player.deck.length - 1] :
                playerCard,
            nextPlayerCard: gameData && gameData.player && gameData.player.deck && 
            gameData.player.deck.length > 1 ?
            gameData.player.deck[gameData.player.deck.length - 2] :
            nextPlayerCard,
            deckCardIndex: 0,
            playerCardIndex: 1,
            nextCardIndex: 2
        });
    }, [gameData, isMultiplayer]);

    const sharedNumber = useMemo<number>(
        () => getSharedNumber(cardState.deckCard, cardState.playerCard), 
    [cardState.deckCard, cardState.playerCard]);

    const onClickSharedNumber = useCallback(() => {
        onClickSharedNumberProp && onClickSharedNumberProp(sharedNumber);

        // Audio
        if (!isMuted) {
            clickSound.play();
        }

        const playerCard = document.getElementById('playerCard');
        const deckCard = document.getElementById('deckCard');
        const originalPlayerCardBoundingRect = playerCard?.getBoundingClientRect();
        const totalTime = 600;
        if (playerCard && deckCard) {
            // Animate the player card to the deck card
            const playerCardBoundingRect = playerCard.getBoundingClientRect();
            const deckCardBoundingRect = deckCard.getBoundingClientRect();
            playerCard.style.transform = `translateY(${60}px)`;
            playerCard.style.transform += `translateX(${0}px)`;
            playerCard.style.transition = 'transform 0.1s';

            setTimeout(function () {
                playerCard.style.transform = `translateY(${deckCardBoundingRect.y - playerCardBoundingRect.y - 60}px)`;
                playerCard.style.transform += `translateX(${deckCardBoundingRect.x - playerCardBoundingRect.x}px)`;
                playerCard.style.transition = 'transform 0.3s';
            }, 100);

            setTimeout(function () {
                playerCard.style.transform = `translateY(${deckCardBoundingRect.y - playerCardBoundingRect.y}px)`;
                playerCard.style.transform += `translateX(${deckCardBoundingRect.x - playerCardBoundingRect.x}px)`;
                playerCard.style.transition = 'transform 0.1s';
            }, 400);
        }
        if (!isMultiplayer) {
            setTimeout(function (){
                if (cardState.nextCardIndex + 1 >= totalCards) {
                    // All done!
                    onAllDone();
                } else {
                    setCardState((prevState: ICardState) => {
                        return {
                            deckCard: randomizedCards[prevState.playerCardIndex],
                            playerCard: randomizedCards[prevState.nextCardIndex],
                            nextPlayerCard: 
                                prevState.nextCardIndex + 1 <= totalCards ? 
                                randomizedCards[prevState.nextCardIndex + 1] :
                                [],
                            deckCardIndex: prevState.playerCardIndex,
                            playerCardIndex: prevState.nextCardIndex,
                            nextCardIndex: prevState.nextCardIndex + 1
                        }
                    });
                }

                if (playerCard && deckCard && originalPlayerCardBoundingRect) {
                    playerCard.style.transform = '';
                    playerCard.style.transition = '';
                }
            }, totalTime);
        } else {
            setTimeout(function (){
                if (playerCard && deckCard && originalPlayerCardBoundingRect) {
                    playerCard.style.transform = '';
                    playerCard.style.transition = '';
                }
            }, totalTime);
        }
    }, [
        randomizedCards, 
        cardState.nextCardIndex, 
        onAllDone, 
        totalCards,
        isMultiplayer,
        onClickSharedNumberProp,
        sharedNumber,
        clickSound,
        isMuted
    ]);

    const deckCard = (
        <Card 
            icons={cardState.deckCard} 
            images={isMultiplayer ? icons : images}
            sharedNumber={sharedNumber}
            onClickSharedNumber={onClickSharedNumber}
            onClickWrongIcon={onClickWrongIcon}
            isDisabled={isDisabled}
        />  
    );

    const playerCard = useMemo(() =>(
        <div>
            <Card 
                icons={cardState.playerCard}
                images={isMultiplayer ? icons : images}
                sharedNumber={sharedNumber}
                onClickSharedNumber={onClickSharedNumber}
                onClickWrongIcon={onClickWrongIcon}
                isDisabled={isDisabled}
                isPlayerCard
            >
                { // Only show the next player card if there are more left
                    cardState.nextPlayerCard && cardState.nextPlayerCard.length > 0 && <Card
                        icons={cardState.nextPlayerCard}
                        images={isMultiplayer ? icons : images}
                        sharedNumber={sharedNumber}
                        onClickSharedNumber={onClickSharedNumber}
                        onClickWrongIcon={onClickWrongIcon}
                        isDisabled={isDisabled}
                        isPlayerCard
                        isNextCard
                    />
                }
            </Card>
        </div>
    ), [
        cardState.playerCard, 
        cardState.nextPlayerCard, 
        sharedNumber, 
        onClickSharedNumber, 
        onClickWrongIcon, 
        isDisabled, 
        icons, 
        images, 
        isMultiplayer
    ]);

    const cardStack = useMemo(() => {
        const numOfPlayerCards: number = isMultiplayer ? (
            gameData && gameData.player && gameData.player.deck.length
        ) : randomizedCards.length - cardState.nextCardIndex;

        return (
            <div className={styles.cardStackContainer}>
                <CardStack numOfPlayerCards={numOfPlayerCards} className={styles.card} />
                {playerCard}
            </div>
        );
    }, [
        playerCard, 
        gameData,
        isMultiplayer,
        cardState.nextCardIndex,
        randomizedCards.length
    ]);

    return (
        <div className={styles.cardContainer}>
            {deckCard}
            {cardStack}
        </div>
    );
}

function randomizeCardsAndIcons(cards: number[][]): number[][] {
    let randomizedCards = cards;
  
    for (let i = randomizedCards.length - 1; i >= 0; i--) {
        let j = Math.floor(Math.random() * (i + 1));
        let temp = randomizedCards[i];
        randomizedCards[i] = randomizedCards[j];
        randomizedCards[i] = randomizeIconsOnCards(randomizedCards[i]);
        randomizedCards[j] = temp;
        randomizedCards[j] = randomizeIconsOnCards(randomizedCards[j]);
    }
  
    return randomizedCards;
}